import { useState } from "react";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import { FiExternalLink } from "react-icons/fi";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { FormField } from "@/components/common/form-field";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { generatePacket, submitOffer } from "@/features/oic/api";
import type { OICFlow, PacketResult } from "@/features/oic/types";

interface Props {
  clientId: string;
  caseId: string;
  clientType: "INDIVIDUAL" | "BUSINESS";
  flow: OICFlow;
  onFlow: (flow: OICFlow) => void;
}

/**
 * Step 5 — Packet / Generate & Attach. The form checklist (656 always; 433-A(OIC)
 * for individuals / 433-B(OIC) for business; 656-PPV optional periodic), each
 * with an "Open editor" seam to the Tier 3.4/3.1 editor. Generate Packet drives
 * the form engine and links the Documents to the Case; Mark-filed records the
 * submission and advances the case.
 */
export function PacketStep({ clientId, caseId, clientType, flow, onFlow }: Props) {
  const [packet, setPacket] = useState<PacketResult | null>(null);
  const [generating, setGenerating] = useState(false);
  const [markOpen, setMarkOpen] = useState(false);

  const cisForm = clientType === "BUSINESS" ? "433-B (OIC)" : "433-A (OIC)";
  const isPeriodic = flow.offerPlan === 24;

  // The checklist: 656 (always, Tier 3.4 seam), the CIS form, + 656-PPV if periodic.
  const checklist = [
    { form: "656", label: "Form 656 — Offer in Compromise", seam: true },
    { form: cisForm, label: `Form ${cisForm} — Collection Information Statement`, seam: false },
    ...(isPeriodic
      ? [{ form: "656-PPV", label: "Form 656-PPV — Periodic Payment Voucher", seam: true }]
      : []),
  ];

  const canGenerate =
    flow.eligibilityAck && !!flow.offerPlan && flow.offerAmountCents != null && !!flow.basis;

  async function onGenerate() {
    setGenerating(true);
    try {
      const res = await generatePacket(caseId);
      setPacket(res.packet);
      onFlow(res.flow);
      toast.success("Packet generated and attached to the case.");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Couldn't generate the packet.");
    } finally {
      setGenerating(false);
    }
  }

  const statusFor = (form: string): "generated" | "pending" | null => {
    if (!packet) return null;
    const hit = packet.forms.find((f) => f.formNumber === form);
    if (hit) return hit.status;
    return packet.pending.includes(form) ? "pending" : null;
  };

  return (
    <div className="grid gap-4">
      {!canGenerate && (
        <Alert>
          <AlertTitle>Finish the earlier steps first</AlertTitle>
          <AlertDescription>
            Acknowledge eligibility and set the plan, amount, and basis before generating.
          </AlertDescription>
        </Alert>
      )}

      <Card className="gap-0 overflow-hidden p-0">
        <CardHeader className="bg-muted/40 p-4">
          <CardTitle className="text-sm">OIC packet forms</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {checklist.map((item) => {
            const st = statusFor(item.form);
            return (
              <div
                key={item.form}
                className="flex items-center justify-between gap-3 border-t px-4 py-3 first:border-t-0"
              >
                <div>
                  <p className="text-sm font-medium">{item.label}</p>
                  {item.seam && (
                    <p className="text-muted-foreground text-xs">
                      Owned by the Tier 3.4 form module — {item.form === "656" ? "editor" : "voucher"}{" "}
                      coming with that build.
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {st === "generated" && <Badge variant="secondary">Generated</Badge>}
                  {st === "pending" && <Badge variant="outline">Pending Tier 3.4</Badge>}
                  {!item.seam && (
                    <Button asChild variant="ghost" size="sm">
                      <Link
                        to={`/client/${clientId}/irs-forms/${encodeURIComponent(item.form)}`}
                      >
                        <FiExternalLink className="size-4" /> Open editor
                      </Link>
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </CardContent>
      </Card>

      <div className="flex flex-wrap items-center gap-2">
        <Button onClick={onGenerate} disabled={!canGenerate || generating}>
          {generating ? "Generating…" : "Generate packet"}
        </Button>
        <Button
          variant="outline"
          disabled={flow.status !== "PACKET_READY" && flow.status !== "SUBMITTED"}
          onClick={() => setMarkOpen(true)}
        >
          Mark OIC as filed
        </Button>
        {flow.status === "SUBMITTED" && (
          <Badge variant="secondary">
            Filed{flow.submittedOn ? ` on ${flow.submittedOn}` : ""}
          </Badge>
        )}
      </div>

      {packet && packet.pending.length > 0 && (
        <p className="text-muted-foreground text-xs">
          Pending forms ({packet.pending.join(", ")}) are owned by the Tier 3.4 form module and
          will generate once that build lands.
        </p>
      )}

      <MarkFiledModal
        caseId={caseId}
        open={markOpen}
        onOpenChange={setMarkOpen}
        onFiled={onFlow}
      />
    </div>
  );
}

const markFiledSchema = Yup.object({
  submittedOn: Yup.string().required("Enter the submission date."),
  irsOfferNumber: Yup.string().max(20, "20 characters max."),
});

function MarkFiledModal({
  caseId,
  open,
  onOpenChange,
  onFiled,
}: {
  caseId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onFiled: (flow: OICFlow) => void;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Mark OIC as filed</DialogTitle>
        </DialogHeader>
        <Formik
          initialValues={{ submittedOn: "", irsOfferNumber: "" }}
          validationSchema={markFiledSchema}
          onSubmit={async (values, helpers) => {
            try {
              const flow = await submitOffer(caseId, {
                submittedOn: values.submittedOn,
                irsOfferNumber: values.irsOfferNumber || undefined,
              });
              onFiled(flow);
              toast.success("OIC marked as filed.");
              onOpenChange(false);
            } catch (err) {
              toast.error(err instanceof Error ? err.message : "Couldn't mark it filed.");
            } finally {
              helpers.setSubmitting(false);
            }
          }}
        >
          {({ isSubmitting }) => (
            <Form className="grid gap-4">
              <FormField name="submittedOn" label="Submission date" type="date" />
              <FormField
                name="irsOfferNumber"
                label="IRS offer number (optional)"
                maxLength={20}
                placeholder="e.g. 1000123456"
              />
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={isSubmitting}>
                  Mark filed
                </Button>
              </DialogFooter>
            </Form>
          )}
        </Formik>
      </DialogContent>
    </Dialog>
  );
}
